import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Application,
  Container,
  Graphics,
  Sprite,
  Text,
  Texture,
  type Ticker,
} from 'pixi.js';
import { playSfx } from '../effects/sound.js';
import { usePixiScene } from './pixi/usePixiScene.js';
import { originalTheme, sceneColor } from '../theme.js';
import type { SceneProps } from './types.js';
import { preferredDensity, preloadPieces, type PieceImages } from '../assets/sprites.js';
import { CARDS_PIECES, CHIP_COLOURS, makeShadowTexture, placeOnCard, placeSuit, type CardsPiece } from './cards/cardsArt.js';

interface CardsPresentation {
  card: string;
  multiplierBp: number;
}

type SuitLetter = 'S' | 'H' | 'D' | 'C';

const PRIMARY_FALLBACK = 0x3a82f6;
const RED = 0xe23b3b;
const NEAR_BLACK = 0x1a1a1a;

const RED_SUITS: ReadonlySet<SuitLetter> = new Set<SuitLetter>(['H', 'D']);
const VALID_RANKS: ReadonlySet<string> = new Set<string>([
  'A', 'K', 'Q', 'J', '10', '9', '8', '7', '6', '5', '4', '3', '2',
]);

// Reveal timeline (ms). charge → flip-out (scale.x 1→0) → swap face → flip-in
// (0→1) → settle. The flip IS the game, so it always plays; reduced motion only
// drops the charge-up + decorative sparkles and uses a shorter flip.
const CHARGE_MS = 380;
const FLIP_HALF_MS = 320; // each half of the flip (out, then in)
const SETTLE_MS = 240;
const FLIP_HALF_REDUCED_MS = 250;
const TURBO_DURATION_SCALE = 0.3;
const LIFT_PX = 18; // charge-up vertical lift

const WIN_EFFECT_MS = 1400;
const SPARKLE_COUNT = 8;

// Outside-the-ticker failsafe for a resolved round whose reveal hasn't fired.
// Chrome stops rAF for hidden/occluded tabs, so the Pixi tick (and the
// in-tick watchdog) can stop entirely mid-round — without this, the round
// would stay `playing` forever (bet debited, button stuck busy) until the tab
// is rendered again. setTimeout keeps firing in hidden tabs (clamped, not
// stopped), so this always settles the round; the scene then catches up via
// its `revealed` branch on the next rendered frame.
const REVEAL_FAILSAFE_MS = 4000;

// Faces are drawn ONCE at this fixed reference size, then a uniform scale on the
// fitWrap container resizes them to the host. The flip animates card.scale.x
// independently of the fit, so a resize can never disturb (or stall) the flip.
const CARD_W = 168;
const CARD_H = 240;
// The host is a 1:1 design square; fit so the card fills it with margin.
const REFERENCE_SIZE = 300;
const MIN_FIT_SCALE = 0.1;

function parseHexColor(input: string | undefined, fallback: number): number {
  if (!input) return fallback;
  const hex = input.trim().replace(/^#/, '');
  if (/^[0-9a-fA-F]{6}$/.test(hex)) return parseInt(hex, 16);
  if (/^[0-9a-fA-F]{3}$/.test(hex)) {
    const r = hex[0];
    const g = hex[1];
    const b = hex[2];
    return parseInt(`${r}${r}${g}${g}${b}${b}`, 16);
  }
  return fallback;
}

function isSuit(c: string): c is SuitLetter {
  return c === 'S' || c === 'H' || c === 'D' || c === 'C';
}

function parseCard(raw: string): { rank: string; suit: SuitLetter } | null {
  if (!raw) return null;
  const suitChar = raw.slice(-1).toUpperCase();
  if (!isSuit(suitChar)) return null;
  const rank = raw.slice(0, -1).toUpperCase();
  if (!VALID_RANKS.has(rank)) return null;
  return { rank, suit: suitChar };
}

function readPresentation(presentation: unknown): CardsPresentation | null {
  if (presentation == null || typeof presentation !== 'object') return null;
  const obj = presentation as Record<string, unknown>;
  if (typeof obj.card !== 'string') return null;
  return {
    card: obj.card,
    multiplierBp: typeof obj.multiplierBp === 'number' ? obj.multiplierBp : 0,
  };
}

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

function easeOutBack(t: number): number {
  const c1 = 1.70158;
  const c3 = c1 + 1;
  return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
}

function easeOutQuad(t: number): number {
  return 1 - (1 - t) * (1 - t);
}

function lighten(color: number, amount: number): number {
  const r = (color >> 16) & 0xff;
  const g = (color >> 8) & 0xff;
  const b = color & 0xff;
  const lr = Math.round(r + (255 - r) * amount);
  const lg = Math.round(g + (255 - g) * amount);
  const lb = Math.round(b + (255 - b) * amount);
  return (lr << 16) | (lg << 8) | lb;
}

// --- Suit drawing (Pixi Graphics, never glyphs) -----------------------------
// Each draws centred at (0,0); `s` is the half-extent (suit fits roughly a 2s box).
// All four use closed bezier silhouettes — assembling suits from overlapping
// circles/triangles reads as disconnected blobs at pip sizes (spade and club
// become indistinguishable).

function drawHeart(g: Graphics, s: number, color: number) {
  const k = s / 6;
  g.moveTo(0, 6 * k)
    .bezierCurveTo(-6.4 * k, 0.6 * k, -5.8 * k, -6 * k, -3 * k, -6 * k)
    .bezierCurveTo(-1.4 * k, -6 * k, -0.2 * k, -4.8 * k, 0, -3.4 * k)
    .bezierCurveTo(0.2 * k, -4.8 * k, 1.4 * k, -6 * k, 3 * k, -6 * k)
    .bezierCurveTo(5.8 * k, -6 * k, 6.4 * k, 0.6 * k, 0, 6 * k)
    .closePath()
    .fill({ color });
}

function drawDiamond(g: Graphics, s: number, color: number) {
  g.poly([0, -s, s * 0.74, 0, 0, s, -s * 0.74, 0]).fill({ color });
}

function drawStem(g: Graphics, s: number, color: number, topY: number) {
  const k = s / 6;
  // flared base: narrow at the body, sweeping out to the feet
  g.moveTo(-2.5 * k, 6 * k)
    .quadraticCurveTo(-0.6 * k, 5.4 * k, -0.55 * k, topY)
    .lineTo(0.55 * k, topY)
    .quadraticCurveTo(0.6 * k, 5.4 * k, 2.5 * k, 6 * k)
    .closePath()
    .fill({ color });
}

function drawSpade(g: Graphics, s: number, color: number) {
  const k = s / 6;
  // inverted-heart body: tip up, round lobes at the bottom
  g.moveTo(0, -6 * k)
    .bezierCurveTo(-6.2 * k, -0.4 * k, -5.6 * k, 4.2 * k, -2.7 * k, 4.2 * k)
    .bezierCurveTo(-1.3 * k, 4.2 * k, -0.3 * k, 3.4 * k, 0, 2.2 * k)
    .bezierCurveTo(0.3 * k, 3.4 * k, 1.3 * k, 4.2 * k, 2.7 * k, 4.2 * k)
    .bezierCurveTo(5.6 * k, 4.2 * k, 6.2 * k, -0.4 * k, 0, -6 * k)
    .closePath()
    .fill({ color });
  drawStem(g, s, color, 2.2 * k);
}

function drawClub(g: Graphics, s: number, color: number) {
  const k = s / 6;
  const lobeR = 2.15 * k;
  g.circle(0, -3.5 * k, lobeR).fill({ color });
  g.circle(-2.45 * k, 0.6 * k, lobeR).fill({ color });
  g.circle(2.45 * k, 0.6 * k, lobeR).fill({ color });
  // centre wedge joins the three lobes into one glyph
  g.poly([0, -3.5 * k, -2.45 * k, 0.6 * k, 2.45 * k, 0.6 * k]).fill({ color });
  // stem reaches the glyph centre: the bottom lobes (r 2.15k at x ±2.45k) leave
  // |x| < ~0.34k uncovered, so a stem starting below the wedge edge (y 0.6k)
  // shows a white notch in the middle of the club
  drawStem(g, s, color, 0);
}

function drawSuit(g: Graphics, suit: SuitLetter, s: number, color: number) {
  switch (suit) {
    case 'H':
      drawHeart(g, s, color);
      break;
    case 'D':
      drawDiamond(g, s, color);
      break;
    case 'S':
      drawSpade(g, s, color);
      break;
    case 'C':
      drawClub(g, s, color);
      break;
  }
}

type Stage = 'idle' | 'charge' | 'flipOut' | 'flipIn' | 'settled';

/** Fresh, uncached textures per scene: the scene's teardown destroys them, the decoded images stay cached. */
function toTextures(imgs: PieceImages<CardsPiece>): Record<CardsPiece, Texture> {
  const out = {} as Record<CardsPiece, Texture>;
  for (const piece of CARDS_PIECES) out[piece] = Texture.from({ resource: imgs[piece], autoGenerateMipmaps: true }, true);
  return out;
}

// The rendered suits' colours, for the ranks the scene writes next to them.
const ART_RED = 0xc40f26;
const ART_INK = 0x1f2340;
// How thick the card shows edge-on, in card units, and the colour of its edge.
const CARD_EDGE = 9;
const EDGE_COLOR = 0xd8ccb6;
const CHIP_POOL = 14;

interface SceneState {
  phase: SceneProps['phase'];
  envelope: SceneProps['envelope'];
  onRevealComplete: () => void;
  turbo: boolean;
}

export function CardsScene({
  config: _config,
  envelope,
  phase,
  onRevealComplete,
  turbo = false,
}: SceneProps) {
  const { t } = useTranslation('originals');

  const stateRef = useRef<SceneState>({ phase, envelope, onRevealComplete, turbo });
  stateRef.current = { phase, envelope, onRevealComplete, turbo };

  useEffect(() => {
    if (phase !== 'playing' || !envelope) return;
    const id = window.setTimeout(() => {
      stateRef.current.onRevealComplete();
    }, REVEAL_FAILSAFE_MS);
    return () => window.clearTimeout(id);
  }, [phase, envelope]);

  const onReady = useRef((app: Application, host: HTMLDivElement) => {
    const primary = parseHexColor(
      getComputedStyle(host).getPropertyValue('--primary').trim() || undefined,
      PRIMARY_FALLBACK,
    );
    // The card back wears the tile's deep teal; the accent stays for glows and the win ring.
    const cardsTheme = originalTheme('cards');
    const backColor = cardsTheme ? sceneColor(cardsTheme.hue) : primary;

    // Container hierarchy:
    //   root    — centred on the host (only its position changes on resize)
    //   fitWrap — uniform scale to fit the host (only its scale changes on resize)
    //   card    — holds back + front faces; the ONLY thing the flip touches
    //             (card.scale.x 0..1). glow/winFx/sparkleFx ride the fit too.
    const root = new Container();
    const fitWrap = new Container();
    app.stage.addChild(root);
    root.addChild(fitWrap);

    const glow = new Graphics(); // soft glow behind the card
    const card = new Container(); // flipped: holds back + front faces
    const back = new Container();
    const front = new Container();
    // Loss dim is an overlay ON TOP of the face — dimming via front.alpha makes
    // every child translucent individually, so the suits' overlapping construction
    // shapes (circles + wedge + stem) and the offset suit shadow show through as
    // seams inside the glyph.
    const dim = new Graphics();
    const winFx = new Graphics(); // win ring + light-sweep
    const sparkleFx = new Graphics(); // radiating diamond sparkles

    card.addChild(back, front, dim);
    // With the 3D art: a soft shadow under the card and the card's edge, seen as it turns.
    const shadow = new Sprite(Texture.EMPTY);
    shadow.anchor.set(0.5);
    shadow.visible = false;
    const edge = new Graphics();
    const chipFx = new Container();
    // The chips fly out from behind the card, so the card that won stays readable.
    fitWrap.addChild(glow, shadow, chipFx, edge, card, winFx, sparkleFx);
    let tex: Record<CardsPiece, Texture> | null = null;
    let shadowTex: Texture | null = null;
    let disposed = false;
    let shownCard = '';
    const chips: { s: Sprite; x: number; y: number; vx: number; vy: number; vr: number; life: number; maxLife: number }[] = [];

    dim
      .roundRect(-CARD_W / 2, -CARD_H / 2, CARD_W, CARD_H, CARD_W * 0.1)
      .fill({ color: 0x000000, alpha: 0.18 });
    dim.visible = false;

    // --- geometry: faces are drawn once at this fixed reference size ---
    const cardW = CARD_W;
    const cardH = CARD_H;

    // --- animation state ---
    // Stage progress is wall-clock (performance.now), NOT accumulated ticker
    // deltaMS: Chrome stops rAF entirely for hidden/occluded tabs, and an
    // accumulated timeline would freeze mid-flip (and replay in slow motion on
    // forced frames). With wall-clock timing the first frame after a stall
    // snaps the flip to where it should already be.
    let stage: Stage = 'idle';
    let stageStart = performance.now();
    const stageElapsedMs = () => performance.now() - stageStart;
    const enterStage = (next: Stage) => {
      stage = next;
      stageStart = performance.now();
    };
    let chargeMs = CHARGE_MS;
    let flipHalfMs = FLIP_HALF_MS;
    let reduced = prefersReducedMotion();
    let startedRoundId: string | null = null;
    let firedRoundId: string | null = null;
    let win = false;
    let winElapsed = 0;
    let idleTime = 0;

    function frontVisible(v: boolean) {
      front.visible = v;
      back.visible = !v;
    }

    function buildBack() {
      back.removeChildren().forEach((c) => c.destroy());
      if (tex) {
        back.addChild(placeOnCard(tex['card-back'], 'card-back', cardH));
        return;
      }
      const w = cardW;
      const h = cardH;
      const r = w * 0.1;
      const body = new Graphics();
      // tinted card body + inner border
      body
        .roundRect(-w / 2, -h / 2, w, h, r)
        .fill({ color: backColor });
      body
        .roundRect(-w / 2, -h / 2, w, h, r)
        .stroke({ width: Math.max(2, w * 0.02), color: lighten(backColor, 0.5), alpha: 0.6 });
      const inset = w * 0.07;
      body
        .roundRect(
          -w / 2 + inset,
          -h / 2 + inset,
          w - inset * 2,
          h - inset * 2,
          r * 0.7,
        )
        .stroke({ width: Math.max(1.5, w * 0.012), color: lighten(backColor, 0.6), alpha: 0.7 });
      back.addChild(body);

      // repeating diamond/diagonal motif, clipped to the inner panel
      const motif = new Graphics();
      const innerX = -w / 2 + inset;
      const innerY = -h / 2 + inset;
      const innerW = w - inset * 2;
      const innerH = h - inset * 2;
      const step = w * 0.14;
      const dColor = lighten(backColor, 0.42);
      for (let y = innerY + step / 2; y < innerY + innerH; y += step) {
        for (let x = innerX + step / 2; x < innerX + innerW; x += step) {
          const d = step * 0.28;
          motif
            .poly([x, y - d, x + d, y, x, y + d, x - d, y])
            .fill({ color: dColor, alpha: 0.45 });
        }
      }
      // mask the motif to the inner rounded panel so diamonds never spill the edge
      const motifMask = new Graphics();
      motifMask
        .roundRect(innerX, innerY, innerW, innerH, r * 0.7)
        .fill({ color: 0xffffff });
      back.addChild(motifMask, motif);
      motif.mask = motifMask;

      // centre medallion
      const med = new Graphics();
      const mr = w * 0.16;
      med
        .circle(0, 0, mr)
        .fill({ color: lighten(backColor, 0.18) })
        .circle(0, 0, mr)
        .stroke({ width: Math.max(1.5, w * 0.014), color: lighten(backColor, 0.7), alpha: 0.85 });
      med
        .circle(0, 0, mr * 0.6)
        .stroke({ width: Math.max(1, w * 0.01), color: lighten(backColor, 0.85), alpha: 0.7 });
      // small inner diamond accent in the medallion
      const md = mr * 0.34;
      med.poly([0, - md, md * 0.7, 0, 0, md, - md * 0.7, 0]).fill({
        color: lighten(backColor, 0.85),
        alpha: 0.9,
      });
      back.addChild(med);
    }

    function buildFront(cardStr: string) {
      front.removeChildren().forEach((c) => c.destroy());
      shownCard = cardStr;
      const parsed = parseCard(cardStr);
      if (tex) {
        buildArtFront(parsed);
        return;
      }
      const w = cardW;
      const h = cardH;
      const r = w * 0.1;

      // white/light card body + drop shadow (flat face — a gloss overlay reads
      // as a stray rounded box on the near-white body, not a highlight)
      const shadow = new Graphics();
      shadow
        .roundRect(-w / 2 + w * 0.02, -h / 2 + h * 0.03, w, h, r)
        .fill({ color: 0x000000, alpha: 0.28 });
      const body = new Graphics();
      body
        .roundRect(-w / 2, -h / 2, w, h, r)
        .fill({ color: 0xf7f7f4 });
      body
        .roundRect(-w / 2, -h / 2, w, h, r)
        .stroke({ width: Math.max(1.5, w * 0.012), color: 0xd9d9d2, alpha: 1 });
      front.addChild(shadow, body);

      if (!parsed) {
        const q = new Text({
          text: '?',
          style: {
            fontFamily: 'system-ui, sans-serif',
            fontSize: w * 0.46,
            fill: NEAR_BLACK,
            fontWeight: '900',
          },
        });
        q.anchor.set(0.5);
        front.addChild(q);
        return;
      }

      const suitColor = RED_SUITS.has(parsed.suit) ? RED : NEAR_BLACK;
      const cornerSize = w * 0.18;
      const cornerInset = w * 0.12;
      // Both indices read upright. The bottom-right one is anchored by its own
      // bottom edge so it stays inside the card without a 180° rotation —
      // rotating the glyph turns ranks like "6" into "9" and reads as two
      // different cards stacked on one face.
      const cornerH = w * 0.2 + cornerSize * 0.34 + cornerSize * 0.32;

      const makeCorner = () => {
        const corner = new Container();
        const rankText = new Text({
          text: parsed.rank,
          style: {
            fontFamily: 'system-ui, sans-serif',
            fontSize: w * 0.2,
            fill: suitColor,
            fontWeight: '900',
            align: 'center',
          },
        });
        rankText.anchor.set(0.5, 0);
        rankText.position.set(0, 0);
        const suitGfx = new Graphics();
        drawSuit(suitGfx, parsed.suit, cornerSize * 0.32, suitColor);
        suitGfx.position.set(0, w * 0.2 + cornerSize * 0.34);
        corner.addChild(rankText, suitGfx);
        return corner;
      };

      const tl = makeCorner();
      tl.position.set(-w / 2 + cornerInset, -h / 2 + cornerInset);
      const br = makeCorner();
      br.position.set(w / 2 - cornerInset, h / 2 - cornerInset - cornerH);

      // large centre suit — flat, no offset shadow copy: a duplicated glyph
      // behind the suit reads as doubled edges on the lobed shapes
      const centre = new Graphics();
      drawSuit(centre, parsed.suit, w * 0.3, suitColor);

      front.addChild(centre, tl, br);
    }

    /** The 3D face: the rendered cream card and suit, the rank written in its corners. */
    function buildArtFront(parsed: ReturnType<typeof parseCard>) {
      if (!tex) return;
      const w = cardW;
      const h = cardH;
      front.addChild(placeOnCard(tex['card-front'], 'card-front', cardH));
      if (!parsed) {
        const q = new Text({ text: '?', style: { fontFamily: 'system-ui, sans-serif', fontSize: w * 0.46, fill: ART_INK, fontWeight: '900' } });
        q.anchor.set(0.5);
        front.addChild(q);
        return;
      }
      const ink = RED_SUITS.has(parsed.suit) ? ART_RED : ART_INK;
      front.addChild(placeSuit(tex[`suit-${parsed.suit.toLowerCase()}` as CardsPiece], parsed.suit, cardH, 0, 0, 1));
      // Both indices read upright (a turned "6" reads as a "9"); the lower one sits on its own foot.
      const corner = (x: number, y: number) => {
        const rank = new Text({
          text: parsed.rank,
          style: { fontFamily: 'system-ui, sans-serif', fontSize: w * 0.19, fill: ink, fontWeight: '900', align: 'center' },
        });
        rank.anchor.set(0.5, 0);
        rank.position.set(x, y);
        const suit = placeSuit(tex![`suit-${parsed.suit.toLowerCase()}` as CardsPiece], parsed.suit, cardH, x, y + w * 0.27, 0.2);
        front.addChild(rank, suit);
      };
      const inset = w * 0.15;
      corner(-w / 2 + inset, -h / 2 + w * 0.08);
      corner(w / 2 - inset, h / 2 - w * 0.08 - w * 0.36);
    }

    function burstChips() {
      if (!tex) return;
      for (let i = 0; i < CHIP_POOL; i++) {
        let c = chips[i];
        if (!c) {
          const sprite = new Sprite(Texture.EMPTY);
          sprite.anchor.set(0.5);
          chipFx.addChild(sprite);
          c = chips[i] = { s: sprite, x: 0, y: 0, vx: 0, vy: 0, vr: 0, life: 0, maxLife: 1 };
        }
        const a = -Math.PI / 2 + (Math.random() - 0.5) * 2.4;
        const speed = 0.22 + Math.random() * 0.28;
        c.s.texture = tex[`chip-${CHIP_COLOURS[i % CHIP_COLOURS.length]}` as CardsPiece];
        c.s.width = c.s.height = cardW * (0.2 + Math.random() * 0.08);
        c.s.visible = true;
        c.s.rotation = Math.random() * Math.PI * 2;
        c.x = (Math.random() - 0.5) * cardW * 0.4;
        c.y = (Math.random() - 0.5) * cardH * 0.3;
        c.vx = Math.cos(a) * speed;
        c.vy = Math.sin(a) * speed;
        c.vr = (Math.random() - 0.5) * 0.012;
        c.life = 0;
        c.maxLife = 1000 + Math.random() * 500;
      }
    }

    function stepChips(dt: number) {
      for (const c of chips) {
        if (!c.s.visible) continue;
        c.life += dt;
        if (c.life >= c.maxLife) {
          c.s.visible = false;
          continue;
        }
        c.vy += 0.0009 * dt;
        c.x += c.vx * dt;
        c.y += c.vy * dt;
        c.s.position.set(c.x, c.y);
        c.s.rotation += c.vr * dt;
        c.s.alpha = Math.min(1, (1 - c.life / c.maxLife) * 2.5);
      }
    }

    /** The card's shadow, edge and shading follow its lift and turn (3D art only). */
    function drawDepth() {
      edge.clear();
      if (!tex) return;
      // Turned share of the face, 0 (edge-on) to 1; the lift grows the card past 1, which is not a turn.
      const sx = Math.min(1, Math.abs(card.scale.x) / Math.max(1e-6, card.scale.y));
      const sy = card.scale.y;
      const lift = Math.max(0, -card.position.y) / LIFT_PX;
      shadow.visible = true;
      shadow.position.set(card.position.x + cardW * 0.04 + lift * 4, cardH * 0.05 + lift * 10);
      shadow.scale.set(sx * (1 + lift * 0.06), sy * (1 + lift * 0.06));
      shadow.alpha = 0.45 - lift * 0.15;
      // Edge-on, the card shows its thickness: on the left while the back turns away, on the right as the face comes round.
      const t = CARD_EDGE * Math.sqrt(Math.max(0, 1 - sx * sx)) * sy;
      if (t > 0.4) {
        const halfW = (cardW / 2) * sx;
        const top = card.position.y - (cardH / 2) * sy + cardW * 0.06;
        const height = cardH * sy - cardW * 0.12;
        const x = stage === 'flipOut' ? card.position.x - halfW - t + 1 : card.position.x + halfW - 1;
        edge.roundRect(x, top, t, height, Math.min(t / 2, 4)).fill({ color: EDGE_COLOR });
        edge.rect(x + (stage === 'flipOut' ? 0 : t * 0.55), top, t * 0.45, height).fill({ color: 0x000000, alpha: 0.12 });
      }
      // A face turned away from the light is darker.
      const shade = Math.round(255 * (0.62 + 0.38 * sx));
      card.tint = (shade << 16) | (shade << 8) | shade;
    }

    // Build the faces ONCE at the fixed reference size. The front is built empty
    // here and re-built once per round in startReveal to show the actual card.
    buildBack();
    buildFront('');
    frontVisible(false);

    // fit() is the ONLY resize handler. It re-centres root and sets a uniform
    // scale on fitWrap to fit the host — it NEVER rebuilds faces, never touches
    // the stage machine, and never touches card.scale.x. The flip animates
    // card.scale.x on a child of fitWrap, so a resize at any moment (including
    // the flipOut→flipIn edge-on boundary) only re-scales the wrapper and can
    // never stall the flip.
    function fit() {
      const w = app.screen.width;
      const h = app.screen.height;
      // Ignore zero/transient sizes the host reports mid-layout: dividing by a
      // 0-size screen would zero the wrapper. Keep the last good fit instead.
      if (w < 2 || h < 2) return;
      const fitScale = Math.max(MIN_FIT_SCALE, Math.min(w, h) / REFERENCE_SIZE);
      fitWrap.scale.set(fitScale);
      root.position.set(w / 2, h / 2);
    }

    fit();
    const onResize = () => fit();
    app.renderer.on('resize', onResize);

    function startReveal(roundId: string, cardStr: string) {
      startedRoundId = roundId;
      reduced = prefersReducedMotion();
      const scale = stateRef.current.turbo ? TURBO_DURATION_SCALE : 1;
      flipHalfMs = (reduced ? FLIP_HALF_REDUCED_MS : FLIP_HALF_MS) * scale;
      chargeMs = CHARGE_MS * scale;
      buildFront(cardStr); // prepare the front face up-front (still hidden)
      frontVisible(false);
      dim.visible = false;
      front.scale.set(1);
      card.scale.set(1);
      card.position.set(0, 0);
      winFx.clear();
      sparkleFx.clear();
      win = false;
      enterStage(reduced ? 'flipOut' : 'charge');
      if (reduced) playSfx('flip');
    }

    function fireReveal(roundId: string) {
      if (firedRoundId === roundId) return;
      firedRoundId = roundId;
      playSfx('reveal');
      stateRef.current.onRevealComplete();
    }

    // Force the flip to its landed state. Used by the watchdog so a resize (or
    // any stall) around the flipOut→flipIn boundary can never leave the card
    // stuck edge-on / invisible with the reveal never firing.
    function forceSettle(env: SceneState['envelope']) {
      const didWin = env?.win === true;
      card.scale.set(1);
      card.position.set(0, 0);
      frontVisible(true);
      dim.visible = !didWin;
      front.scale.set(1);
      stage = 'settled';
      stageStart = performance.now() - SETTLE_MS; // skip the bounce
      win = didWin;
      winElapsed = 0;
      if (env) fireReveal(env.roundId);
    }

    // Show an already-resolved round instantly (e.g. mount straight into
    // `revealed`, or a resize rebuild after the flip) without re-animating.
    function settleInstant(roundId: string, cardStr: string, didWin: boolean) {
      startedRoundId = roundId;
      firedRoundId = roundId;
      buildFront(cardStr);
      frontVisible(true);
      dim.visible = !didWin;
      front.scale.set(1);
      card.scale.set(1);
      card.position.set(0, 0);
      stage = 'settled';
      stageStart = performance.now() - SETTLE_MS; // skip the bounce
      win = didWin;
      winElapsed = 0;
    }

    function drawGlow(intensity: number) {
      glow.clear();
      if (intensity <= 0) return;
      const radius = Math.max(cardW, cardH) * 0.62;
      // layered soft glow rings
      glow.circle(0, 0, radius).fill({ color: primary, alpha: 0.1 * intensity });
      glow
        .circle(0, 0, radius * 0.78)
        .fill({ color: lighten(primary, 0.2), alpha: 0.14 * intensity });
      glow
        .circle(0, 0, radius * 0.56)
        .fill({ color: lighten(primary, 0.35), alpha: 0.16 * intensity });
    }

    function drawWinRing(intensity: number) {
      // Around the 3D card, outside its edge: drawn on the card's own outline it covers the face.
      const out = tex ? cardW * 0.07 : 0;
      const w = cardW + out * 2;
      const h = cardH + out * 2;
      const r = cardW * 0.1 + out;
      winFx.clear();
      if (intensity <= 0) return;
      winFx
        .roundRect(-w / 2, -h / 2, w, h, r)
        .stroke({
          width: Math.max(2, w * 0.03),
          color: primary,
          alpha: 0.9 * intensity,
        });
      winFx
        .roundRect(
          -w / 2 - w * 0.04,
          -h / 2 - w * 0.04,
          w + w * 0.08,
          h + w * 0.08,
          r * 1.2,
        )
        .stroke({
          width: Math.max(1, w * 0.018),
          color: lighten(primary, 0.3),
          alpha: 0.5 * intensity,
        });
    }

    function drawSparkles(p: number) {
      sparkleFx.clear();
      if (p <= 0 || p >= 1) return;
      const reach = Math.max(cardW, cardH) * 0.62;
      for (let i = 0; i < SPARKLE_COUNT; i++) {
        const a = (i / SPARKLE_COUNT) * Math.PI * 2 + 0.35;
        const delay = (i % 3) * 0.08;
        const local = Math.min(1, Math.max(0, (p - delay) / (1 - delay)));
        if (local <= 0) continue;
        const dist = reach * (0.4 + 0.6 * easeOutQuad(local));
        const fade = local < 0.5 ? local * 2 : (1 - local) * 2;
        const sx = Math.cos(a) * dist;
        const sy = Math.sin(a) * dist;
        const sr = cardW * 0.06 * (1 - local * 0.4);
        sparkleFx
          .poly([sx, sy - sr, sx + sr * 0.45, sy, sx, sy + sr, sx - sr * 0.45, sy])
          .fill({ color: lighten(primary, 0.4), alpha: 0.9 * fade });
      }
    }

    const tick = (tk: Ticker) => {
      const dt = tk.deltaMS;
      const { phase: ph, envelope: env } = stateRef.current;
      idleTime += dt;

      const pres = env ? readPresentation(env.outcome.presentation) : null;

      // Reset to idle when we leave a round.
      if (ph === 'idle') {
        if (stage !== 'idle') {
          stage = 'idle';
          frontVisible(false);
          card.scale.set(1);
          winFx.clear();
          sparkleFx.clear();
          win = false;
          winElapsed = 0;
        }
        startedRoundId = null;
        firedRoundId = null;
      } else if (env && pres) {
        // A new round to reveal (fresh idle→playing, or a back-to-back round
        // after a settled one): kick off the flip for the new roundId.
        if (
          ph === 'playing' &&
          startedRoundId !== env.roundId &&
          (stage === 'idle' || stage === 'settled')
        ) {
          startReveal(env.roundId, pres.card);
        } else if (
          ph === 'revealed' &&
          firedRoundId !== env.roundId &&
          stage !== 'flipOut' &&
          stage !== 'flipIn' &&
          stage !== 'charge'
        ) {
          // arrived already-revealed (no flip seen) → settle instantly
          settleInstant(env.roundId, pres.card, env.win === true);
        }
      }

      // --- watchdog: never let an in-progress flip hang ---
      // If we are still revealing this round but the flip phase has run far past
      // its expected duration (e.g. the ticker was throttled mid-swap, or a
      // backgrounded tab starved frames), snap to the settled state so the front
      // always shows and onRevealComplete always fires. Resize no longer touches
      // the flip at all, but the watchdog stays as a belt-and-braces guarantee.
      if (
        env &&
        (stage === 'charge' || stage === 'flipOut' || stage === 'flipIn')
      ) {
        const expected = stage === 'charge' ? chargeMs : flipHalfMs;
        if (stageElapsedMs() > expected * 4) {
          forceSettle(env);
          if (win) playSfx('coin');
        }
      }

      // --- advance the active reveal stage ---
      if (stage === 'charge') {
        const p = Math.min(1, stageElapsedMs() / chargeMs);
        const e = easeOutQuad(p);
        card.position.y = -LIFT_PX * e;
        card.scale.set(1 + 0.05 * e);
        drawGlow(0.65 * e);
        if (p >= 1) {
          enterStage('flipOut');
          playSfx('flip');
        }
      } else if (stage === 'flipOut') {
        // collapse to edge-on: scale.x 1 → 0
        const p = Math.min(1, stageElapsedMs() / flipHalfMs);
        const e = easeInOutCubic(p);
        card.scale.x = 1 - e;
        if (!reduced) card.position.y = -LIFT_PX * (1 - e);
        if (p >= 1) {
          // edge-on: swap the visible face from back to front
          card.scale.x = 0;
          frontVisible(true);
          enterStage('flipIn');
        }
      } else if (stage === 'flipIn') {
        // expand from edge-on: scale.x 0 → 1
        const p = Math.min(1, stageElapsedMs() / flipHalfMs);
        card.scale.x = easeInOutCubic(p);
        if (p >= 1) {
          card.scale.x = 1;
          enterStage('settled');
          // landing: fire reveal exactly once, decide win effects
          if (env) {
            win = env.win === true;
            winElapsed = 0;
            fireReveal(env.roundId);
            if (win) playSfx('coin');
            if (win && !reduced) burstChips();
          }
        }
      } else if (stage === 'settled') {
        // settle bounce on scale.y, glow eases out for a loss / sustains on win
        const settleElapsed = stageElapsedMs();
        if (!reduced && settleElapsed < SETTLE_MS) {
          const p = settleElapsed / SETTLE_MS;
          card.scale.y = 1 + 0.06 * (1 - easeOutBack(p));
          card.position.y = -LIFT_PX * 0.2 * (1 - easeOutQuad(p));
        } else {
          card.scale.y = 1;
          card.position.y = 0;
        }

        if (win) {
          winElapsed += dt;
          const wp = Math.min(1, winElapsed / WIN_EFFECT_MS);
          // sustained pulsing ring
          const ringPulse = 0.6 + 0.4 * (0.5 + 0.5 * Math.sin(idleTime / 240));
          drawWinRing(ringPulse);
          // light-sweep handled via card breathing scale + glow pulse
          const glowPulse = 0.5 + 0.3 * (0.5 + 0.5 * Math.sin(idleTime / 300));
          drawGlow(glowPulse);
          if (!reduced) drawSparkles(wp);
          else sparkleFx.clear();
          // rank/suit gentle pulse
          const pulse = 1 + 0.03 * Math.sin(idleTime / 220);
          front.scale.set(pulse);
        } else {
          // loss: settle plainly, slight dim, no ring/sparkles
          winFx.clear();
          sparkleFx.clear();
          front.scale.set(1);
          dim.visible = true;
          const g = Math.max(0, 0.3 - settleElapsed / 1200);
          drawGlow(g);
        }
      } else if (stage === 'idle') {
        // gentle float + inviting pulsing glow on the face-down card
        dim.visible = false;
        front.scale.set(1);
        card.scale.set(1);
        card.position.y = Math.sin(idleTime / 700) * 6;
        const pulse = 0.28 + 0.18 * (0.5 + 0.5 * Math.sin(idleTime / 520));
        drawGlow(reduced ? 0.3 : pulse);
        winFx.clear();
        sparkleFx.clear();
      }
    };

    const depthTick = (tk: Ticker) => {
      stepChips(tk.deltaMS);
      drawDepth();
    };
    app.ticker.add(tick);
    app.ticker.add(depthTick);

    void preloadPieces('cards', CARDS_PIECES, preferredDensity(window.devicePixelRatio)).then((imgs) => {
      if (!imgs || disposed) return;
      tex = toTextures(imgs);
      shadowTex = makeShadowTexture(cardW, cardH, cardW * 0.1, 10);
      shadow.texture = shadowTex;
      buildBack();
      buildFront(shownCard);
      dim.clear().roundRect(-CARD_W / 2, -CARD_H / 2, CARD_W, CARD_H, CARD_W * 0.08).fill({ color: 0x000000, alpha: 0.18 });
    });

    return () => {
      disposed = true;
      if (tex) for (const t3d of Object.values(tex)) t3d.destroy(true);
      shadowTex?.destroy(true);
      tex = null;
      app.ticker.remove(tick);
      app.ticker.remove(depthTick);
      app.renderer.off('resize', onResize);
    };
  }).current;

  // Start fetching the art with the component, ahead of the Pixi init the scene waits on.
  useEffect(() => {
    void preloadPieces('cards', CARDS_PIECES, preferredDensity(window.devicePixelRatio));
  }, []);

  const hostRef = usePixiScene(onReady);

  return (
    <div className="relative w-full lg:min-h-[clamp(340px,60vh,560px)] originals-scene-fit [--orig-reserve:580px] overflow-hidden">
      <div ref={hostRef} className="absolute inset-0" aria-hidden />
      {phase === 'idle' && (
        <span className="absolute bottom-4 text-[12px] font-semibold uppercase tracking-wide text-[var(--text-muted)]">
          {t('cards.flip')}
        </span>
      )}
    </div>
  );
}
